"use client";

import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import Link from "next/link";
import Image from "next/image";
import ReviewTestimonialCard from "@/components/reviews/ReviewTestimonialCard";
import ChevronLeftIcon from "../icons/ChevronLeftIcon";
import ChevronRightIcon from "../icons/ChevronRightIcon";
import { getStoredStoreId } from "@/lib/store-storage";
import { DANK_OPEN_PICKUP_STORE_MODAL } from "@/lib/header-events";
import type { GooglePlaceReviewsData, GooglePlaceReviewRow } from "@/lib/api/store-place-details";
import { Button, ModalCloseButton, ModalPortal } from "@/components";
import { useScrollLock } from "@/lib/use-scroll-lock";

// ─── Button style constants ────────────────────────────────────────────────────

const MODAL_SEGMENT_BASE =
  "flex-1 rounded-lg px-3 py-2 text-sm font-semibold transition-colors";
const MODAL_SEGMENT_ACTIVE = "bg-blue-80 text-constant-white";
const MODAL_SEGMENT_INACTIVE = "text-text-secondary";

const MODAL_ACTION_BTN =
  "inline-flex items-center justify-center rounded-xl border border-neutral-10 bg-bg-1 px-3 py-2 text-sm font-semibold text-blue-100 transition-colors";

type Testimonial = {
  name: string;
  time: string;
  rating: number;
  review: string;
  avatar: string;
};

export type { GooglePlaceReviewsData } from "@/lib/api/store-place-details";

type ReviewsSectionProps = {
  /**
   * When set (e.g. location detail page), Google reviews use this store.
   * Otherwise uses the store id from the shopping cookie.
   */
  covaLocationId?: number | null;
  /**
   * When provided (including `null`), skips the client place-details fetch.
   * Pass from the server so the browser does not call the API directly.
   */
  initialGoogleReviews?: GooglePlaceReviewsData | null;
  /**
   * `/reviews` page supplies its own hero; hide the in-section title row and redundant “See all reviews” CTAs.
   */
  dedicatedPage?: boolean;
};

export default function ReviewsSection({
  covaLocationId: covaLocationIdProp = null,
  initialGoogleReviews,
  dedicatedPage = false,
}: ReviewsSectionProps = {}) {
  const DESKTOP_REVIEWS_PER_ROW = 10;
  const DESKTOP_TOTAL_REVIEWS = DESKTOP_REVIEWS_PER_ROW * 2;
  const [currentSlide, setCurrentSlide] = useState(0);
  const [userInteracted, setUserInteracted] = useState(false);
  const mobileScrollerRef = useRef<HTMLDivElement | null>(null);
  const serverSeededReviews = initialGoogleReviews !== undefined;
  const initialStoreId =
    covaLocationIdProp != null && Number.isFinite(Number(covaLocationIdProp))
      ? Number(covaLocationIdProp)
      : null;
  const [googleReviews, setGoogleReviews] = useState<GooglePlaceReviewsData | null>(() =>
    serverSeededReviews ? initialGoogleReviews ?? null : null
  );
  const [loadingReviews, setLoadingReviews] = useState(false);
  const [selectedStoreId, setSelectedStoreId] = useState<number | null>(null);
  const [showLeaveReviewModal, setShowLeaveReviewModal] = useState(false);
  const [reviewLinkCopied, setReviewLinkCopied] = useState(false);
  const [reviewChannel, setReviewChannel] = useState<"delivery" | "instore">("delivery");
  useScrollLock(showLeaveReviewModal);
  const base = process.env.NEXT_PUBLIC_API_BASE_URL || "http://localhost:4000";
  /**
   * Every shop, each with its own Google write-a-review link.
   *
   * The grid used to be eight labels reading "Dank delivery listing A" through
   * H, all pointing at whichever single place id the section had loaded, so
   * every button opened the same form and none of them named a real shop.
   */
  const [reviewStores, setReviewStores] = useState<
    Array<{ covaLocationId: number; name: string; city?: string | null; googlePlaceId?: string | null }>
  >([]);

  useEffect(() => {
    if (!showLeaveReviewModal || reviewStores.length > 0) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${base}/api/stores`, { cache: "no-store" });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled && Array.isArray(data?.stores)) setReviewStores(data.stores);
      } catch {
        // The modal still works: the buttons fall back to the store picker.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showLeaveReviewModal, reviewStores.length, base]);

  const listingsWithReviewUrl = useMemo(
    () =>
      reviewStores
        .filter((s) => Boolean(s.googlePlaceId))
        .map((s) => ({
          key: String(s.covaLocationId),
          label: s.city ? `${s.name} - ${s.city}` : s.name,
          href: `https://search.google.com/local/writereview?placeid=${encodeURIComponent(
            String(s.googlePlaceId)
          )}`,
        })),
    [reviewStores]
  );

  // Always prefer store currently selected in header; fallback to explicit page store id.
  const reviewStoreId = useMemo(() => {
    if (selectedStoreId && Number.isFinite(selectedStoreId) && selectedStoreId > 0) {
      return selectedStoreId;
    }
    const n = covaLocationIdProp != null ? Number(covaLocationIdProp) : NaN;
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [selectedStoreId, covaLocationIdProp]);

  const formatReviewCountPlus = useCallback((count?: number | null) => {
    const n = Number(count || 0);
    if (!Number.isFinite(n) || n <= 0) return null;
    if (n >= 100) return `${(Math.floor(n / 100) * 100).toLocaleString()}+`;
    if (n >= 10) return `${Math.floor(n / 10) * 10}+`;
    return `${Math.floor(n)}+`;
  }, []);

  useEffect(() => {
    if (initialGoogleReviews === undefined) return;
    setGoogleReviews(initialGoogleReviews ?? null);
  }, [initialGoogleReviews]);

  // Update selectedStoreId from storage and listen for changes
  useEffect(() => {
    const updateStoreId = () => {
      const storeId = getStoredStoreId();
      setSelectedStoreId(storeId);
    };

    // Get initial store ID
    updateStoreId();

    // Listen for storage changes (when store is changed in header)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'dank_store_id' || e.key === null) {
        updateStoreId();
      }
    };

    // Listen for custom store change events (in case header uses custom events)
    const handleStoreChange = () => {
      updateStoreId();
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('store-changed', handleStoreChange);

    // Poll for store ID changes (since cookies don't trigger storage events)
    const interval = setInterval(updateStoreId, 1000);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('store-changed', handleStoreChange);
      clearInterval(interval);
    };
  }, []);

  // Fetch Google reviews via backend for the active header-selected store.
  // If server-seeded reviews exist for the same store, use them and skip initial fetch.
  useEffect(() => {
    const shouldUseServerSeedOnly =
      serverSeededReviews &&
      reviewStoreId != null &&
      initialStoreId != null &&
      reviewStoreId === initialStoreId;
    if (shouldUseServerSeedOnly) return;

    const fetchStoreReviews = async () => {
      if (!reviewStoreId) {
        setGoogleReviews(null);
        return;
      }

      setLoadingReviews(true);
      try {
        const res = await fetch(`${base}/api/stores/${reviewStoreId}/place-details?limit=50&t=${Date.now()}`, {
          cache: "no-store",
        });

        if (!res.ok) {
          console.warn(
            `[ReviewsSection] Failed to fetch reviews for store ${reviewStoreId}: ${res.status} ${res.statusText}`
          );
          setGoogleReviews(null);
          return;
        }

        const data = await res.json();

        if (data.success && data.place) {
          const placeData: GooglePlaceReviewsData = {
            place_id: data.place.place_id,
            rating: data.place.rating,
            user_ratings_total: data.place.user_ratings_total,
            reviews:
              data.place.reviews?.map((review: Record<string, unknown>) => ({
                author_name: String(review.author_name || ""),
                author_url: review.author_url as string | undefined,
                profile_photo_url: review.profile_photo_url as string | undefined,
                rating: Number(review.rating || 0),
                relative_time_description: String(review.relative_time_description || ""),
                text: String(review.text || ""),
                time: Number(review.time || 0),
              })) || [],
          };

          setGoogleReviews(placeData);
        } else {
          console.warn(`[ReviewsSection] No reviews data for store ${reviewStoreId}:`, data);
          setGoogleReviews(null);
        }
      } catch (error) {
        const msg = error instanceof Error ? error.message : String(error);
        console.warn(
          `[ReviewsSection] Could not load Google reviews for store ${reviewStoreId} (is the API reachable at ${base}?):`,
          msg
        );
        setGoogleReviews(null);
      } finally {
        setLoadingReviews(false);
      }
    };

    fetchStoreReviews();
  }, [reviewStoreId, base, serverSeededReviews, initialStoreId]);


  // Transform Google reviews to testimonials format
  const transformGoogleReviewsToTestimonials = (reviews: GooglePlaceReviewRow[]): Testimonial[] => {
    return reviews.map((review) => ({
      name: review.author_name,
      time: review.relative_time_description,
      rating: review.rating,
      review: review.text,
      avatar: review.profile_photo_url || `https://i.pravatar.cc/150?img=${Math.floor(Math.random() * 70) + 1}`,
    }));
  };

  // Real reviews only (no synthetic fallback testimonials)
  const allTestimonials = useMemo(() => {
    if (googleReviews?.reviews && googleReviews.reviews.length > 0) {
      const transformed = transformGoogleReviewsToTestimonials(googleReviews.reviews);
      const unique: Testimonial[] = [];
      const seen = new Set<string>();
      for (const item of transformed) {
        const key = `${item.name}::${item.time}::${item.review}`.trim().toLowerCase();
        if (!seen.has(key)) {
          seen.add(key);
          unique.push(item);
        }
      }
      return unique.slice(0, DESKTOP_TOTAL_REVIEWS);
    }
    return [];
  }, [googleReviews, DESKTOP_TOTAL_REVIEWS]);

  const reviewCountLabel = useMemo(() => {
    const formatted = formatReviewCountPlus(googleReviews?.user_ratings_total);
    return formatted || "3000+";
  }, [googleReviews?.user_ratings_total, formatReviewCountPlus]);
  const googleReviewUrl = useMemo(() => {
    if (googleReviews?.place_id) {
      return `https://search.google.com/local/writereview?placeid=${encodeURIComponent(googleReviews.place_id)}`;
    }
    if (process.env.NEXT_PUBLIC_GOOGLE_REVIEW_URL) {
      return process.env.NEXT_PUBLIC_GOOGLE_REVIEW_URL;
    }
    return "";
  }, [googleReviews?.place_id]);

  // Split testimonials into two rows for desktop marquee
  const displayTestimonialsRow1 = useMemo(
    () => allTestimonials.slice(0, DESKTOP_REVIEWS_PER_ROW),
    [allTestimonials, DESKTOP_REVIEWS_PER_ROW]
  );
  const displayTestimonialsRow2 = useMemo(
    () =>
      allTestimonials.slice(
        DESKTOP_REVIEWS_PER_ROW,
        DESKTOP_REVIEWS_PER_ROW * 2
      ),
    [allTestimonials, DESKTOP_REVIEWS_PER_ROW]
  );

  // One card per slide on mobile
  const totalSlides = allTestimonials.length;

  const scrollToSlide = useCallback((index: number) => {
    const scroller = mobileScrollerRef.current;
    if (!scroller) return;
    const card = scroller.children[index] as HTMLElement | undefined;
    if (!card) return;
    scroller.scrollTo({ left: card.offsetLeft, behavior: "smooth" });
  }, []);

  const goToSlide = useCallback((index: number) => {
    setUserInteracted(true);
    setCurrentSlide(index);
    scrollToSlide(index);
  }, [scrollToSlide]);

  const goToPrev = useCallback(() => {
    goToSlide((currentSlide - 1 + totalSlides) % totalSlides);
  }, [currentSlide, totalSlides, goToSlide]);

  const goToNext = useCallback(() => {
    goToSlide((currentSlide + 1) % totalSlides);
  }, [currentSlide, totalSlides, goToSlide]);

  // Sync active dot when user manually swipes
  const handleMobileScroll = useCallback(() => {
    const scroller = mobileScrollerRef.current;
    if (!scroller) return;
    const cardWidth = (scroller.children[0] as HTMLElement | undefined)?.offsetWidth ?? 0;
    if (!cardWidth) return;
    const index = Math.round(scroller.scrollLeft / cardWidth);
    setCurrentSlide(index);
  }, []);

  // Auto-advance, paused once the user has interacted
  useEffect(() => {
    if (userInteracted || totalSlides <= 1) return;
    const timer = setInterval(() => {
      setCurrentSlide((prev) => {
        const next = (prev + 1) % totalSlides;
        scrollToSlide(next);
        return next;
      });
    }, 5000);
    return () => clearInterval(timer);
  }, [userInteracted, totalSlides, scrollToSlide]);

  useEffect(() => {
    if (!showLeaveReviewModal) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setShowLeaveReviewModal(false);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [showLeaveReviewModal]);

  const openStorePicker = useCallback(() => {
    window.dispatchEvent(new CustomEvent(DANK_OPEN_PICKUP_STORE_MODAL));
  }, []);

  const handleCopyReviewLink = useCallback(async () => {
    if (!googleReviewUrl) return;
    try {
      await navigator.clipboard.writeText(googleReviewUrl);
      setReviewLinkCopied(true);
      setTimeout(() => setReviewLinkCopied(false), 1800);
    } catch {
      setReviewLinkCopied(false);
    }
  }, [googleReviewUrl]);

  const renderReviewCard = (testimonial: Testimonial, key: string, widthClass: string = "w-full") => (
    <ReviewTestimonialCard key={key} testimonial={testimonial} widthClass={widthClass} />
  );

  return (
    <section
      className="w-full px-4 sm:px-6 lg:px-8"
      aria-label="Customer Reviews"
    >
      <div className="w-full flex flex-col gap-8 max-sm:gap-4">
        {/* Header, hidden on dedicated /reviews (page hero covers it) */}
        {!dedicatedPage ? (
        <header className="flex flex-col items-center gap-3.5 max-w-[648px] mx-auto">
          <div className="relative flex items-center justify-center">
            <Image
              src="/media/sections/reviews/decorative/rice-plant.svg"
              alt=""
              width={70}
              height={116}
              className="w-[70px] sm:h-[116px] h-[84px]"
              aria-hidden="true"
            />
            <h2 className="text-center sm:text-h3-desktop text-h4-mobile font-bold text-text-primary max-w-[550px] px-4">
              {`Best Calgary dispensaries rated by ${reviewCountLabel} reviews`}
            </h2>
            <Image
              src="/media/sections/reviews/decorative/rice-plant.svg"
              alt=""
              width={70}
              height={116}
              className="w-[70px] sm:h-[116px] h-[84px] scale-x-[-1]"
              aria-hidden="true"
            />
          </div>

          <div className="flex items-center gap-4 max-sm:hidden">
            <Button
              type="button"
              variant="secondary"
              size="lg"
              className="rounded-xl px-6"
              onClick={() => setShowLeaveReviewModal(true)}
            >
              Leave review
            </Button>

            <Button
              asChild
              variant="primary"
              size="lg"
              className="rounded-xl px-6"
            >
              <Link href="/reviews">See all reviews</Link>
            </Button>
          </div>
        </header>
        ) : (
          <div className="hidden sm:flex flex-wrap items-center justify-end gap-3">
            <Button
              type="button"
              variant="secondary"
              size="lg"
              className="rounded-xl px-6"
              onClick={() => setShowLeaveReviewModal(true)}
            >
              Leave review
            </Button>
          </div>
        )}

        {/* Mobile Carousel */}
        {allTestimonials.length > 0 ? (
        <div className="sm:hidden">
          {/* Snap-scroll track */}
          <div
            ref={mobileScrollerRef}
            className="flex snap-x snap-mandatory overflow-x-auto scrollbar-hide gap-3 pb-1"
            onScroll={handleMobileScroll}
          >
            {allTestimonials.map((testimonial, index) => (
              <div
                key={`mobile-${index}`}
                className="w-[calc(100%-24px)] shrink-0 snap-start"
              >
                {renderReviewCard(testimonial, `mobile-card-${index}`)}
              </div>
            ))}
          </div>

          {/* Nav row: prev · dots · next */}
          {totalSlides > 1 && (
            <div className="mt-4 flex items-center justify-between gap-3 px-1">
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label="Previous review"
                onClick={goToPrev}
                className="h-9 w-9 rounded-full"
              >
                <ChevronLeftIcon size={16} />
              </Button>

              <div className="flex flex-1 items-center justify-center gap-1.5 overflow-x-auto scrollbar-hide py-1">
                {allTestimonials.map((_, index) => (
                  <Button
                    raw
                    key={index}
                    type="button"
                    aria-label={`Go to review ${index + 1}`}
                    onClick={() => goToSlide(index)}
                    className={`h-2 shrink-0 rounded-full transition-all duration-200 ${
                      currentSlide === index
                        ? "w-5 bg-blue-100"
                        : "w-2 bg-neutral-15"
                    }`}
                  />
                ))}
              </div>

              <Button
                variant="ghost"
                size="icon-sm"
                aria-label="Next review"
                onClick={goToNext}
                className="h-9 w-9 rounded-full"
              >
                <ChevronRightIcon size={16} />
              </Button>
            </div>
          )}
        </div>
        ) : null}

        {/* Desktop Marquee */}
        {allTestimonials.length > 0 ? (
        <div className="hidden sm:block relative overflow-hidden">
          <div className="flex flex-col gap-4">
            {/* Row 1 */}
            <div className="flex gap-4 animate-marquee-row1">
              {/* Original set */}
              {displayTestimonialsRow1.map((testimonial, index) =>
                renderReviewCard(testimonial, `row1-${index}`, "w-[438px]")
              )}
              {/* Duplicate set for seamless loop */}
              {displayTestimonialsRow1.map((testimonial, index) =>
                renderReviewCard(
                  testimonial,
                  `row1-duplicate-${index}`,
                  "w-[438px]"
                )
              )}
            </div>

            {/* Row 2 - Offset by 170px */}
            {displayTestimonialsRow2.length > 0 ? (
              <div
                className="flex gap-4 animate-marquee-row2"
                style={{ marginLeft: "-170px" }}
              >
                {/* Original set */}
                {displayTestimonialsRow2.map((testimonial, index) =>
                  renderReviewCard(testimonial, `row2-${index}`, "w-[438px]")
                )}
                {/* Duplicate set for seamless loop */}
                {displayTestimonialsRow2.map((testimonial, index) =>
                  renderReviewCard(
                    testimonial,
                    `row2-duplicate-${index}`,
                    "w-[438px]"
                  )
                )}
              </div>
            ) : null}
          </div>

          {/* Gradient overlays */}
          <div className="absolute top-0 left-0 w-[150px] h-full bg-gradient-to-r from-[#F7F7F8] to-transparent pointer-events-none z-10" />
          <div className="absolute top-0 right-0 w-[150px] h-full bg-gradient-to-l from-[#F7F7F8] to-transparent pointer-events-none z-10" />
        </div>
        ) : null}

        {allTestimonials.length === 0 ? (
          <div className="rounded-2xl border border-neutral-10 bg-constant-white p-6 text-center text-sm text-text-secondary">
            {loadingReviews
              ? "Loading real Google reviews for selected location..."
              : reviewStoreId
                ? "No live Google reviews available for the selected location yet."
                : "Select a location in the header to load real reviews for that store."}
          </div>
        ) : null}

        <div className="flex flex-col items-center gap-4 sm:hidden">
          {!dedicatedPage ? (
            <Button
              asChild
              variant="primary"
              size="lg"
              className="w-full rounded-xl"
            >
              <Link href="/reviews">See all reviews</Link>
            </Button>
          ) : null}

          <Button
            type="button"
            variant="secondary"
            size="lg"
            className="w-full rounded-xl"
            onClick={() => setShowLeaveReviewModal(true)}
          >
            Leave review
          </Button>
        </div>
      </div>

      {showLeaveReviewModal ? (
        <ModalPortal>
        <div
          className="fixed inset-0 z-[1000] flex items-end justify-center bg-[#07063CBF] p-4 sm:items-center"
          onClick={() => setShowLeaveReviewModal(false)}
          aria-label="Leave review modal overlay"
        >
          <div
            className="relative w-full max-w-xl overflow-hidden rounded-2xl border border-neutral-10 bg-constant-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="leave-review-modal-title"
          >
            <ModalCloseButton
              onClick={() => setShowLeaveReviewModal(false)}
              className="absolute right-5 top-5 z-10"
              aria-label="Close leave review modal"
            />

            <div className="space-y-4 px-4 pb-4 pt-8 sm:px-6 sm:pb-5 sm:pt-9">
              <div className="flex justify-center">
                <Image
                  src="/media/brand/logo.webp"
                  width={130}
                  height={67}
                  alt="Dank"
                  className="h-auto w-[130px]"
                />
              </div>
              <h3
                id="leave-review-modal-title"
                className="text-center text-h4-mobile sm:text-h3-desktop font-bold text-text-primary"
              >
                Show us some 💛!
              </h3>
              <p className="text-center text-body-sm leading-relaxed text-text-secondary">
                {reviewChannel === "delivery"
                  ? "Thanks for ordering with our delivery team! Please choose any listing below."
                  : "Thanks for visiting us! Please choose the shop you stopped by."}
              </p>

              <div className="rounded-xl border border-neutral-10 bg-constant-white p-1 flex">
                <Button
                  variant="ghost"
                  size="md"
                  onClick={() => setReviewChannel("delivery")}
                  className={`${MODAL_SEGMENT_BASE} ${reviewChannel === "delivery" ? MODAL_SEGMENT_ACTIVE : MODAL_SEGMENT_INACTIVE}`}
                >
                  Delivery order
                </Button>
                <Button
                  variant="ghost"
                  size="md"
                  onClick={() => setReviewChannel("instore")}
                  className={`${MODAL_SEGMENT_BASE} ${reviewChannel === "instore" ? MODAL_SEGMENT_ACTIVE : MODAL_SEGMENT_INACTIVE}`}
                >
                  In-store visit
                </Button>
              </div>

              {/* Same grid of shops either way. Which tab you are on changes the
                  wording above it, not where a review can be left: Google has
                  one listing per shop and both a delivery order and a visit are
                  reviewed against it. */}
              {listingsWithReviewUrl.length > 0 ? (
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {listingsWithReviewUrl.map((listing) => (
                    <a
                      key={listing.key}
                      href={listing.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center rounded-xl border border-neutral-10 bg-constant-white px-3 py-2.5 text-center text-sm font-semibold text-blue-100 transition-colors hover:border-blue-60 hover:bg-blue-5"
                    >
                      {listing.label}
                    </a>
                  ))}
                </div>
              ) : (
                <div className="rounded-xl border border-neutral-10 bg-bg-1 px-4 py-3">
                  <p className="text-sm text-text-secondary">
                    {reviewStoreId
                      ? "Leave a review for your selected in-store location."
                      : "Select your store first, then leave your in-store feedback."}
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      className="rounded-xl"
                      onClick={openStorePicker}
                    >
                      Select store
                    </Button>
                    {googleReviewUrl ? (
                      <Button
                        asChild
                        variant="primary"
                        size="sm"
                        className="rounded-xl"
                      >
                        <a href={googleReviewUrl} target="_blank" rel="noopener noreferrer">
                          Leave Google review
                        </a>
                      </Button>
                    ) : null}
                  </div>
                </div>
              )}

              <p className="text-center text-xs leading-relaxed text-text-secondary">
                Note: Please avoid editing existing reviews, as Google may hide them. Instead, leave a review on a different listing.
              </p>

              <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                <Button variant="ghost" size="md" onClick={openStorePicker} className={MODAL_ACTION_BTN}>
                  Find a store
                </Button>
                <a href="tel:+15876666666" className={MODAL_ACTION_BTN}>Call us</a>
                <a href="mailto:hello@dank.ca" className={MODAL_ACTION_BTN}>Contact us</a>
              </div>

              <div className="flex justify-end">
                <Button
                  variant="text"
                  size="sm"
                  onClick={handleCopyReviewLink}
                  className="!h-auto !p-0"
                >
                  {reviewLinkCopied ? "Copied review link" : "Copy review link"}
                </Button>
              </div>
            </div>
          </div>
        </div>
        </ModalPortal>
      ) : null}

      <style jsx>{`
        @keyframes marquee-left {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-50%);
          }
        }

        @keyframes marquee-right {
          0% {
            transform: translateX(-50%);
          }
          100% {
            transform: translateX(0);
          }
        }

        .animate-marquee-row1 {
          animation: marquee-left 24s linear infinite;
        }

        .animate-marquee-row2 {
          animation: marquee-right 26s linear infinite;
        }
      `}</style>
    </section>
  );
}
